/**
 * Frontend API Invocation Helper
 *
 * Replaces `supabase.functions.invoke()` with same-origin fetch to `/api/`.
 * Returns the same `{ data, error }` shape for minimal changes at call sites.
 *
 * Usage:
 * ```typescript
 * // Before:
 * const { data, error } = await supabase.functions.invoke('my-function', { body: { foo: 1 } });
 *
 * // After:
 * const { data, error } = await invokeApi('my-function', { foo: 1 });
 * ```
 */

import { supabase } from './supabase';

export async function invokeApi<T = any>(
  fn: string,
  body?: object,
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const res = await fetch(`/api/${fn}`, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Read the body as text first, then attempt to parse. Gateway errors
    // (Vercel 502/504 timeouts) return a non-JSON body — parsing that with
    // res.json() throws an opaque "Unexpected token … is not valid JSON",
    // hiding the real failure. Parse defensively so callers get a meaningful
    // message instead.
    const raw = await res.text();
    let parsed: any = null;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = null;
      }
    }

    if (!res.ok) {
      const timedOut = res.status === 504 || res.status === 502;
      const message =
        parsed?.error ||
        parsed?.message ||
        (timedOut
          ? `The server took too long to respond (${res.status}). Try a narrower selection or retry.`
          : `Request failed (${res.status} ${res.statusText || 'error'})`);
      return { data: null, error: new Error(message) };
    }

    if (parsed === null && raw) {
      return {
        data: null,
        error: new Error('Received a malformed (non-JSON) response from the server.'),
      };
    }

    return { data: parsed as T, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}
